import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { reviewSchema, type ReviewCreateInput } from "@ckgo2/shared/schemas";
import { api, type ApiResponse } from "@/api/client";
import type { Trip, Guide, Destination } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2, Star } from "lucide-react";
import MediaPicker from "@/components/common/MediaPicker";
import ErrorBanner from "@/components/common/ErrorBanner";

interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: string;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  images?: string[];
  imageIds?: string[];
  isApproved: boolean;
}

function ReviewCustomerSection({
  register,
  errors,
}: {
  register: ReturnType<typeof useForm<ReviewCreateInput>>["register"];
  errors: ReturnType<typeof useForm<ReviewCreateInput>>["formState"]["errors"];
}) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Customer information
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="customerName" className="label">
            Customer name *
          </label>
          <input
            {...register("customerName")}
            type="text"
            id="customerName"
            className={`input ${errors.customerName ? "input-error" : ""}`}
            placeholder="e.g. John Smith"
          />
          {errors.customerName && (
            <p className="text-error-600 mt-1 text-sm">{errors.customerName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="customerLocation" className="label">
            City/Location
          </label>
          <input
            {...register("customerLocation")}
            type="text"
            id="customerLocation"
            className="input"
            placeholder="e.g. New York"
          />
        </div>
      </div>
    </div>
  );
}

function ReviewContentSection({
  register,
  errors,
  rating,
  onRatingChange,
}: {
  register: ReturnType<typeof useForm<ReviewCreateInput>>["register"];
  errors: ReturnType<typeof useForm<ReviewCreateInput>>["formState"]["errors"];
  rating: number;
  onRatingChange: (value: number) => void;
}) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Review content</h2>

      <div className="space-y-4">
        <div>
          <label className="label">Rating *</label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onRatingChange(value)}
                className="p-1"
              >
                <Star
                  className={`h-8 w-8 transition-colors ${
                    value <= rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-300 hover:text-yellow-300"
                  }`}
                />
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-500">{rating} of 5</span>
          </div>
        </div>

        <div>
          <label htmlFor="title" className="label">
            Review title
          </label>
          <input
            {...register("title")}
            type="text"
            id="title"
            className="input"
            placeholder="e.g. Unforgettable experience!"
          />
        </div>

        <div>
          <label htmlFor="content" className="label">
            Review text *
          </label>
          <textarea
            {...register("content")}
            id="content"
            rows={6}
            className={`input ${errors.content ? "input-error" : ""}`}
            placeholder="Write the customer review..."
          />
          {errors.content && (
            <p className="text-error-600 mt-1 text-sm">{errors.content.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="tripDate" className="label">
            Trip date
          </label>
          <input
            {...register("tripDate", { valueAsDate: true })}
            type="date"
            id="tripDate"
            className="input"
          />
        </div>
      </div>
    </div>
  );
}

function ReviewSidebar({
  register,
  trips,
  guides,
  destinations,
}: {
  register: ReturnType<typeof useForm<ReviewCreateInput>>["register"];
  trips: Trip[];
  guides: Guide[];
  destinations: Destination[];
}) {
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Connections</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="tripId" className="label">
              Trip
            </label>
            <select {...register("tripId")} id="tripId" className="input">
              <option value="">-- Select trip --</option>
              {trips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="guideId" className="label">
              Guide
            </label>
            <select {...register("guideId")} id="guideId" className="input">
              <option value="">-- Select guide --</option>
              {guides.map((guide) => (
                <option key={guide.id} value={guide.id}>
                  {guide.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="destinationId" className="label">
              Destination
            </label>
            <select {...register("destinationId")} id="destinationId" className="input">
              <option value="">-- Select destination --</option>
              {destinations.map((dest) => (
                <option key={dest.id} value={dest.id}>
                  {dest.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              {...register("isApproved")}
              type="checkbox"
              id="isApproved"
              className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            />
            <label
              htmlFor="isApproved"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Approved for display
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              {...register("isFeatured")}
              type="checkbox"
              id="isFeatured"
              className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            />
            <label
              htmlFor="isFeatured"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Show as featured
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReviewEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ReviewCreateInput>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      customerName: "",
      customerLocation: "",
      rating: 5,
      title: "",
      content: "",
      tripId: "",
      guideId: "",
      destinationId: "",
      isFeatured: false,
      isVideo: false,
      videoUrl: "",
      imageIds: [],
      isApproved: false,
    },
  });

  // Fetch review if editing
  const { data: review, isLoading } = useQuery({
    queryKey: ["review", id],
    queryFn: () => api.get<ApiResponse<Review>>(`/api/admin/reviews/${id}`),
    enabled: !isNew,
  });

  // Fetch trips for select
  const { data: tripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/trips"),
  });

  // Fetch guides for select
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  // Fetch destinations for select
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  const trips = tripsData?.data || [];
  const guides = guidesData?.data || [];
  const destinations = destinationsData?.data || [];

  // Reset form when review data loads
  useEffect(() => {
    if (review?.data) {
      reset({
        ...review.data,
        tripId: review.data.tripId || "",
        guideId: review.data.guideId || "",
        destinationId: review.data.destinationId || "",
        images: review.data.images || [],
        imageIds: review.data.imageIds || [],
        tripDate: review.data.tripDate ? new Date(review.data.tripDate) : undefined,
      });
    }
  }, [review, reset]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: ReviewCreateInput) => {
      // Clean up empty strings to undefined
      const cleanData = {
        ...data,
        tripId: data.tripId || undefined,
        guideId: data.guideId || undefined,
        destinationId: data.destinationId || undefined,
        videoUrl: data.videoUrl || undefined,
      };

      if (isNew) {
        return api.post<ApiResponse<Review>>("/api/admin/reviews", cleanData);
      }
      return api.put<ApiResponse<Review>>(`/api/admin/reviews/${id}`, cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review", id] });
      navigate("/reviews");
    },
  });

  const onSubmit = async (data: ReviewCreateInput) => {
    await saveMutation.mutateAsync(data);
  };

  const rating = watch("rating");
  const isVideo = watch("isVideo");

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reviews" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New review" : "Edit review"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new review" : `Editing review from ${review?.data?.customerName}`}
            </p>
          </div>
        </div>

        <button
          type="submit"
          form="review-form"
          disabled={isSubmitting || (!isDirty && !isNew)}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Form */}
      <form
        id="review-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          <ReviewCustomerSection register={register} errors={errors} />

          <ReviewContentSection
            register={register}
            errors={errors}
            rating={rating}
            onRatingChange={(value) => setValue("rating", value, { shouldDirty: true })}
          />

          {/* Video */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Video review
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  {...register("isVideo")}
                  type="checkbox"
                  id="isVideo"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isVideo"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  This is a video review
                </label>
              </div>

              {isVideo && (
                <div>
                  <label htmlFor="videoUrl" className="label">
                    Video URL
                  </label>
                  <input
                    {...register("videoUrl")}
                    type="url"
                    id="videoUrl"
                    className="input"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Review Photos */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Review Photos
            </h2>
            <p className="mb-4 text-sm text-gray-500">Upload photos from the customer's trip</p>
            <MediaPicker
              mode="multiple"
              value={(watch("imageIds") as string[] | undefined) || []}
              onChange={(ids) => setValue("imageIds", ids, { shouldDirty: true })}
              folder="general"
            />
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <ReviewSidebar
          register={register}
          trips={trips}
          guides={guides}
          destinations={destinations}
        />
      </form>
    </div>
  );
}
